'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { strings } from '@/content/strings';

type GalleryImage = {
  id: string;
  url: string;
  caption?: string | null;
  room?: string | null;
  order?: number;
};

type RoomGroup = {
  room: string;
  images: GalleryImage[];
};

const IMAGE_EXTENSION_PATTERN = /\.(jpg|jpeg|png|gif|webp|svg|avif|heic)$/i;

const getFileNameFromUrl = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith('data:')) return null;
  try {
    const parsed = new URL(trimmed, 'http://local');
    const base = parsed.pathname.split('/').pop();
    return base ? decodeURIComponent(base) : null;
  } catch {
    return null;
  }
};

const getDisplayCaption = (caption: string | null | undefined, url: string) => {
  const trimmed = (caption ?? '').trim();
  if (!trimmed) return null;
  const fileName = getFileNameFromUrl(url);
  if (fileName) {
    const normalized = fileName.toLowerCase().split('?')[0].split('#')[0];
    if (trimmed.toLowerCase() === normalized) return null;
    const withoutExtension = normalized.replace(IMAGE_EXTENSION_PATTERN, '');
    if (trimmed.toLowerCase() === withoutExtension) return null;
  }
  if (IMAGE_EXTENSION_PATTERN.test(trimmed) && !trimmed.includes(' ')) return null;
  return trimmed;
};

export default function RoomGallery({ images }: { images: GalleryImage[] }) {
  const propertyStrings = strings.screens.property;
  const rooms = useMemo<RoomGroup[]>(() => {
    const sorted = [...images].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const map = new Map<string, GalleryImage[]>();
    sorted.forEach((img) => {
      const label = (img.room || '').trim() || propertyStrings.generalRoom;
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(img);
    });
    return Array.from(map.entries()).map(([room, imgs]) => ({ room, images: imgs }));
  }, [images]);

  const [lightbox, setLightbox] = useState<{ roomIndex: number; imageIndex: number } | null>(null);
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightbox(null);
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightbox]);

  useEffect(() => {
    const body = document.body;
    if (!lightbox) {
      delete body.dataset.modalOpen;
      body.style.overflow = '';
      return;
    }
    body.dataset.modalOpen = 'true';
    body.style.overflow = 'hidden';
    return () => {
      delete body.dataset.modalOpen;
      body.style.overflow = '';
    };
  }, [lightbox]);

  const activeRoom = lightbox ? rooms[lightbox.roomIndex] : null;
  const activeImage = activeRoom ? activeRoom.images[lightbox!.imageIndex] : null;
  const activeCaption = activeImage ? getDisplayCaption(activeImage.caption, activeImage.url) : null;

  const goNext = () => {
    if (!lightbox) return;
    const room = rooms[lightbox.roomIndex];
    if (!room) return;
    const isLastInRoom = lightbox.imageIndex >= room.images.length - 1;
    if (!isLastInRoom) {
      setLightbox({ roomIndex: lightbox.roomIndex, imageIndex: lightbox.imageIndex + 1 });
      return;
    }
    const nextRoomIndex = (lightbox.roomIndex + 1) % rooms.length;
    const nextRoom = rooms[nextRoomIndex];
    if (!nextRoom || nextRoom.images.length === 0) return;
    setLightbox({ roomIndex: nextRoomIndex, imageIndex: 0 });
  };

  const goPrev = () => {
    if (!lightbox) return;
    const room = rooms[lightbox.roomIndex];
    if (!room) return;
    const isFirstInRoom = lightbox.imageIndex <= 0;
    if (!isFirstInRoom) {
      setLightbox({ roomIndex: lightbox.roomIndex, imageIndex: lightbox.imageIndex - 1 });
      return;
    }
    const prevRoomIndex = (lightbox.roomIndex - 1 + rooms.length) % rooms.length;
    const prevRoom = rooms[prevRoomIndex];
    if (!prevRoom || prevRoom.images.length === 0) return;
    setLightbox({ roomIndex: prevRoomIndex, imageIndex: prevRoom.images.length - 1 });
  };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  };

  const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    if (!start || event.changedTouches.length === 0) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = Date.now() - start.time;
    touchStart.current = null;
    if (elapsed > 700) return;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) {
      goNext();
    } else {
      goPrev();
    }
  };

  if (images.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {propertyStrings.noPhotos}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {rooms.map((group, roomIndex) => (
        <div key={group.room} className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">{group.room}</h3>
            <span className="text-xs text-muted-foreground">
              {propertyStrings.photosCount(group.images.length)}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {group.images.map((img, imageIndex) => {
              const displayCaption = getDisplayCaption(img.caption, img.url);
              return (
              <button
                key={`${img.id ?? img.url}-${imageIndex}`}
                type="button"
                onClick={() => setLightbox({ roomIndex, imageIndex })}
                className="group relative overflow-hidden rounded-xl border border-border bg-ghost"
              >
                <img
                  src={img.url}
                  alt={displayCaption || propertyStrings.photoAlt(group.room)}
                  className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
                {displayCaption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/45 text-white text-xs px-2 py-1">
                    {displayCaption}
                  </div>
                )}
              </button>
            );
            })}
          </div>
        </div>
      ))}

      {lightbox && activeImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black px-4 overscroll-contain"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 rounded-full bg-white/20 text-white px-3 py-1 text-sm"
          >
            {propertyStrings.closeGallery}
          </button>
          <button
            type="button"
            onClick={goPrev}
            className="flex absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/20 text-white w-10 h-10 items-center justify-center"
            aria-label={propertyStrings.previousPhoto}
          >
            ‹
          </button>
          <div className="max-w-4xl w-full">
            <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <img
                src={activeImage.url}
                alt={activeCaption || activeRoom?.room || propertyStrings.roomPhotoAlt}
                className="w-full max-h-[80vh] object-contain rounded-xl bg-black"
              />
            </div>
            <div className="mt-3 text-sm text-white/90 flex items-center justify-between">
              <div>
                <div className="font-semibold">{activeRoom?.room}</div>
                {activeCaption && <div className="text-white/70">{activeCaption}</div>}
              </div>
              <div className="text-xs text-white/70">
                {propertyStrings.photoIndex(lightbox.imageIndex + 1, activeRoom?.images.length || 1)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={goNext}
            className="flex absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/20 text-white w-10 h-10 items-center justify-center"
            aria-label={propertyStrings.nextPhoto}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
